import { NAV_SECTIONS } from '@/lib/nav';

/**
 * Route -> page title and subtitle for the top bar.
 *
 * Titles come from the nav model where a route has a nav entry, so a rename in one
 * place cannot leave the sidebar and the page heading disagreeing. Subtitles are
 * declared here because they explain the page rather than label the link, and they
 * are the difference between a heading and a heading that tells you what you can
 * do — "Search and filter every call" in the Frontline reference.
 */

export interface PageMeta {
  title: string;
  subtitle?: string;
}

const SUBTITLES: Record<string, string> = {
  '/dev-connect':
    'Friday, one hour, three or four demos. Claim a slot to show what you shipped - booking closes the day before.',
  '/dashboard': 'What needs you today',
  '/goals': 'Your organization goals, KRAs and development goals',
  '/assessments': 'Quarterly self assessments and their review status',
  '/recognition': 'Recognition you have received and given',
  '/team': 'Your direct reports and their cycle progress',
  '/team/reviews': 'Assessments waiting on your review',
  '/team/kra': 'Write KRAs and weight them into groups totalling 100%',
  '/team/meetings': '1-on-1 discussions and meeting notes',
  '/department': 'Department-wide performance and calibration, read only',
  '/hr/employees': 'Onboard employees, assign managers, departments and roles',
  '/hr/cycles': 'Financial year cycles, quarters and submission windows',
  '/team/goals': 'Who still needs goals — assign a KRA group and publish the drafts',
  '/hr/kra-groups': 'The KRA library — named sets of weighted goals, written once and reused',
  '/hr/reviews': 'Assessments awaiting HR review for fairness and completeness',
  '/hr/calibration': 'Stack rank, target distribution and band moves for the quarter or the year',
  '/hr/analytics': 'Ratings, goal completion, skill gaps and promotion candidates',
  '/hr/pip': 'Performance improvement plans and their check-ins',
  '/admin/final-ratings': 'The annual appraisal: final rating, overall feedback and promotion recommendations',
  '/admin/organization': 'Departments, designations and reporting structure',
  '/admin/audit': 'Every change to goals, ratings and roles',
};

/** Built once from the nav model: href -> title. */
const TITLES: Record<string, string> = Object.fromEntries(
  NAV_SECTIONS.flatMap((section) => section.items.map((item) => [item.href, item.title])),
);

/** Titles for routes that have no sidebar entry (detail pages, login). */
const EXTRA_TITLES: Record<string, string> = {
  '/login': 'Sign in',
  '/onboarding-pending': 'Not onboarded yet',
};

function titleize(segment: string): string {
  const spaced = segment.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function pageMeta(pathname: string): PageMeta {
  const exact = TITLES[pathname] ?? EXTRA_TITLES[pathname];
  if (exact) {
    const subtitle = SUBTITLES[pathname];
    return subtitle ? { title: exact, subtitle } : { title: exact };
  }

  // Detail routes (/team/reviews/:id) fall back to their parent's title until the
  // page publishes something better, so the bar is never blank while loading.
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length - 1; i > 0; i -= 1) {
    const parent = `/${segments.slice(0, i).join('/')}`;
    const parentTitle = TITLES[parent];
    if (parentTitle) return { title: parentTitle };
  }

  const last = segments[segments.length - 1];
  return { title: last ? titleize(last) : 'CargoFL Performance' };
}
